import React, { FunctionComponent, useMemo } from 'react';

import { setHtml } from 'Modules/ui';
import { Box, Typography, ChevronsLeft24, ChevronsRight24, TypographyProps } from 'Modules/ui3';

import { WithContentSize } from '../types';

/*
    BLOCK: quote
    PARAMS: text: string
            caption: string
    EXAMPLE:
    {
        "type" : "quote",
        "data" : {
            "text" : "The unexamined life is not worth living.",
            "caption" : "Socrates"
        }
    }
 */

type QuoteRendererProps = WithContentSize & {
    text: string;
    caption: string;
};

export const QuoteRenderer: FunctionComponent<QuoteRendererProps> = ({ text, caption, size }) => {
    const { textProps, captionProps } = useMemo<Record<'textProps' | 'captionProps', TypographyProps<'p'>>>(() => ({
        textProps: {
            fontSize: {
                desktop: '20px',
                mobile: '16px',
            }[size],
            lineHeight: {
                desktop: '1.5',
                mobile: '1.4',
            }[size],
        },
        captionProps: {
            fontSize: {
                desktop: '16px',
                mobile: '12px',
            }[size],
        },
    }), [size]);

    return (
        <Box
            position='relative'
            width='fit-content'
            mx={8}
            my={5}
        >
            <ChevronsLeft24
                sx={{
                    position: 'absolute',
                    top: '0',
                    left: '-32px',
                    fontSize: '24px',
                    color: 'general.primary50',
                }}
            />
            <Typography
                fontWeight='500'
                component='p'
                color='grayscale.shade95'
                {...textProps}
                {...setHtml(text)}
            />
            <Typography
                fontWeight='400'
                lineHeight='1.5'
                component='p'
                mt={3}
                color='grayscale.shade90'
                {...captionProps}
                {...setHtml(caption)}
            />
            <ChevronsRight24
                sx={{
                    position: 'absolute',
                    right: '-32px',
                    bottom: '0',
                    fontSize: '24px',
                    color: 'general.primary50',
                }}
            />
        </Box>
    );
};
